import { useCallback, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { pagesService } from '../services/pagesService';
import { contentService } from '../services/contentService';
import { invalidateAfterContentChange, queryKeys } from '../api/queryClient';
import { useAuth } from './useAuth';
import { useDismissableError } from './useDismissableError';

export function useAdminContent(filter = 'all', q = '') {
  const { applyViewer } = useAuth();
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);

  const query = useQuery({
    queryKey: queryKeys.adminContent(filter, q),
    queryFn: async () => {
      const data = await pagesService.adminContent(filter, q);
      if (data?.viewer) applyViewer(data.viewer);
      return data;
    },
    staleTime: 30_000,
  });

  const invalidate = useCallback(() => {
    return invalidateAfterContentChange(queryClient);
  }, [queryClient]);

  const run = useCallback(
    async (action) => {
      setBusy(true);
      try {
        const result = await action();
        await invalidate();
        return result;
      } finally {
        setBusy(false);
      }
    },
    [invalidate],
  );

  const { error, setError } = useDismissableError(query.error);

  const createMutation = useMutation({
    mutationFn: (body) => contentService.create(body),
    onSuccess: () => invalidate(),
  });

  return {
    items: query.data?.items ?? [],
    loading:
      query.isLoading ||
      (query.isFetching && !(query.data?.items?.length)),
    error,
    busy: busy || createMutation.isPending,
    setError,
    load: () => query.refetch(),
    run,
    create: (body) => createMutation.mutateAsync(body),
    submit: (id) => run(() => contentService.submit(id)),
    approve: (id) => run(() => contentService.approve(id)),
    reject: (id, note) => run(() => contentService.reject(id, note)),
    publish: (id) => run(() => contentService.publish(id)),
    unpublish: (id) => run(() => contentService.unpublish(id)),
    remove: (id) => run(() => contentService.remove(id)),
    update: (id, body) => run(() => contentService.update(id, body)),
  };
}
